"use client";

import { colors } from "@/common/theme";
import { setTypography } from "@/mono/lib/styles/mixins";
import { mediaMobile } from "@/mono/lib/responsive";
import { easeInOutExpo } from "@/mono/lib/styles/easing";
import styled, { css } from "styled-components";
import { useCallback, useRef, useState } from "react";

const ERROR_COLOR = "#E74C3C";

function TextInput({ name, value, onChange, onBlur, hasError, placeholder }) {
  return (
    <StyledInput
      type="text"
      name={name}
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      $hasError={hasError}
      $hasValue={!!value}
      placeholder={placeholder}
    />
  );
}

function EmailInput({ name, value, onChange, onBlur, hasError, placeholder }) {
  return (
    <StyledInput
      type="email"
      name={name}
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      $hasError={hasError}
      $hasValue={!!value}
      placeholder={placeholder}
    />
  );
}

function LongTextInput({ name, value, onChange, onBlur, hasError, placeholder }) {
  return (
    <StyledTextarea
      name={name}
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      $hasError={hasError}
      $hasValue={!!value}
      rows={5}
      placeholder={placeholder}
    />
  );
}

function SelectInput({ name, value, onChange, onBlur, hasError, options = "", placeholder }) {
  const parsedOptions = typeof options === "string"
    ? options.split(",").map((o) => o.trim()).filter(Boolean)
    : Array.isArray(options) ? options : [];

  return (
    <SelectWrapper $hasError={hasError}>
      <StyledSelect
        name={name}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        $hasError={hasError}
        $hasValue={!!value}
        $isEmpty={!value}
      >
        <option value="" disabled hidden>
          {placeholder || "Selecteer..."}
        </option>
        {parsedOptions.map((opt) => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
      </StyledSelect>
      <ChevronIcon>
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="1.5" />
        </svg>
      </ChevronIcon>
    </SelectWrapper>
  );
}

function CheckboxGroup({ name, label, value = [], onChange, hasError, required, options = "" }) {
  const parsedOptions = typeof options === "string"
    ? options.split(",").map((o) => o.trim()).filter(Boolean)
    : Array.isArray(options) ? options : [];

  const selected = Array.isArray(value) ? value : [];

  const handleToggle = (opt) => {
    const next = selected.includes(opt)
      ? selected.filter((v) => v !== opt)
      : [...selected, opt];
    onChange({ target: { name, value: next } });
  };

  return (
    <div>
      <Label $hasError={hasError}>
        {label}
        {required && "*"}
      </Label>
      <CheckboxGroupList>
        {parsedOptions.map((opt) => {
          const checked = selected.includes(opt);
          return (
            <CheckboxWrapper key={opt} $hasError={hasError} onClick={(e) => { e.preventDefault(); handleToggle(opt); }}>
              <HiddenCheckbox
                type="checkbox"
                name={name}
                checked={checked}
                readOnly
              />
              <StyledCheckbox $checked={checked} $hasError={hasError}>
                {checked && (
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M5 12L10 17L19 7" stroke="currentColor" strokeWidth="2" />
                  </svg>
                )}
              </StyledCheckbox>
              <CheckboxLabel>{opt}</CheckboxLabel>
            </CheckboxWrapper>
          );
        })}
      </CheckboxGroupList>
    </div>
  );
}

export default function FormField({
  field,
  value,
  error,
  onChange,
  onBlur,
}) {
  const { label, name, fieldType, required, options, placeholder } = field;

  if (fieldType === "checkbox") {
    return (
      <FieldWrapper $width={field.width}>
        <CheckboxGroup
          name={name}
          label={label}
          value={value}
          onChange={onChange}
          hasError={!!error}
          required={required}
          options={options}
        />
        {error && <ErrorText>{error}</ErrorText>}
      </FieldWrapper>
    );
  }

  const inputProps = {
    name,
    value: value || "",
    onChange,
    onBlur,
    hasError: !!error,
    options,
    placeholder: placeholder || "",
  };

  return (
    <FieldWrapper $width={field.width}>
      <Label $hasError={!!error}>
        {label}
        {required && "*"}
      </Label>
      {fieldType === "text" && <TextInput {...inputProps} />}
      {fieldType === "email" && <EmailInput {...inputProps} />}
      {fieldType === "longtext" && <LongTextInput {...inputProps} />}
      {fieldType === "select" && <SelectInput {...inputProps} />}
      {error && <ErrorText>{error}</ErrorText>}
    </FieldWrapper>
  );
}

const FieldWrapper = styled.div`
  grid-column: ${({ $width }) => ($width === "half" ? "span 1" : "span 2")};

  ${mediaMobile(css`
    grid-column: span 2;
  `)}
`;

const Label = styled.label`
  display: block;
  ${setTypography("smallParagraph")}
  color: ${({ $hasError, theme }) =>
    $hasError ? ERROR_COLOR : theme.theme.textColor};
  margin-bottom: 15rem;
  transition: color 0.3s ${easeInOutExpo};
`;

const inputBase = css`
  width: 100%;
  background: transparent;
  border: 1px solid
    ${({ $hasError, $hasValue, theme }) =>
      $hasError
        ? ERROR_COLOR
        : $hasValue
          ? theme.theme.textColor
          : theme.theme.backgroundAccent};
  border-radius: 15rem;
  padding: 25rem 30rem;
  color: ${({ theme }) => theme.theme.textColor};
  ${setTypography("smallParagraph")}
  font-weight: 400;
  outline: none;
  transition: border-color 0.3s ${easeInOutExpo};

  &::placeholder {
    color: ${({ theme }) => theme.theme.inactiveTextColor};
    opacity: 0.5;
    font-weight: 400;
  }

  &:focus {
    border-color: ${({ $hasError, theme }) =>
      $hasError ? ERROR_COLOR : theme.theme.textColor};
  }
`;

const StyledInput = styled.input`
  ${inputBase}
`;

const StyledTextarea = styled.textarea`
  ${inputBase}
  resize: vertical;
  min-height: 150rem;
`;

const SelectWrapper = styled.div`
  position: relative;
`;

const StyledSelect = styled.select`
  ${inputBase}
  appearance: none;
  cursor: pointer;
  padding-right: 60rem;

  color: ${({ $isEmpty, $hasError, theme }) =>
    $isEmpty ? theme.theme.inactiveTextColor : theme.theme.textColor};
`;

const ChevronIcon = styled.div`
  position: absolute;
  right: 25rem;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  width: 24rem;
  height: 24rem;

  svg {
    width: 100%;
    height: 100%;
    color: ${({ theme }) => theme.theme.textColor};
  }
`;

const ErrorText = styled.span`
  display: block;
  color: ${ERROR_COLOR};
  ${setTypography("smallerParagraph")}
  margin-top: 8rem;
`;

const CheckboxGroupList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15rem;
`;

const CheckboxWrapper = styled.label`
  display: flex;
  align-items: center;
  gap: 15rem;
  cursor: pointer;
`;

const HiddenCheckbox = styled.input`
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
  pointer-events: none;
`;

const StyledCheckbox = styled.div`
  width: 40rem;
  height: 40rem;
  min-width: 40rem;
  border-radius: 8rem;
  border: 1px solid
    ${({ $hasError, theme }) =>
      $hasError ? ERROR_COLOR : theme.theme.textColor};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: border-color 0.3s ${easeInOutExpo},
    background 0.3s ${easeInOutExpo};
  background: ${({ $checked, theme }) =>
    $checked ? theme.theme.textColor : "transparent"};

  svg {
    width: 70%;
    height: 70%;
    color: ${({ theme }) => theme.theme.background};
  }
`;

const CheckboxLabel = styled.span`
  ${setTypography("smallParagraph")}
  color: ${({ theme }) => theme.theme.textColor};
`;
